import { useState } from 'react';
import type { NoteBody } from '@brimo/shared';
import { Banner } from '../../../ds/components';
import { errorMessage } from '../../../lib/api';
import { createNote } from '../api';
import { EditorCard, PrimaryAction } from './EditorCard';
import { EditorField, FollowUpField, validTime } from './fields';
import { followUpOf, isValidation, type EditorProps } from './shared';
import styles from './editor.module.css';

interface Errors {
  text?: string | undefined;
  followUp?: string | undefined;
}

/** Kontaktakte II.E8 „Notiz": internal text plus optional follow-up → `POST /contacts/:id/notes`. */
export function NoteEditor({ record, onClose, onDone }: EditorProps) {
  const [text, setText] = useState('');
  const [followUp, setFollowUp] = useState(false);
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [errors, setErrors] = useState<Errors>({});
  const [banner, setBanner] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const firstLine = text.trim().split('\n')[0] ?? '';
  const taskTitle = `Follow-up zu: Notiz ${firstLine || record.header.name}`;

  async function submit() {
    const next: Errors = {};
    if (text.trim() === '') next.text = 'Bitte einen Text angeben.';
    if (followUp && !validTime(time)) next.followUp = 'Bitte eine Uhrzeit im Format HH:MM angeben.';
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    const body: NoteBody = { text: text.trim() };
    const due = followUpOf(followUp, date, time);
    if (due) body.followUp = due;
    setPending(true);
    setBanner(null);
    try {
      await createNote(record.id, body);
      onDone({ toast: 'Notiz gespeichert.' });
    } catch (error) {
      if (isValidation(error)) {
        const fields = error.fields;
        setErrors({ text: fields['text'], followUp: fields['followUp.dueOn'] ?? fields['followUp.dueTime'] });
        if (!fields['text'] && !fields['followUp.dueOn'] && !fields['followUp.dueTime'])
          setBanner(error.message);
      } else setBanner(errorMessage(error));
    } finally {
      setPending(false);
    }
  }

  return (
    <EditorCard
      tone="quiet"
      dot="var(--sand-400)"
      title="Notiz"
      subtitle="nur intern"
      footnote="Notizen sind für den Kunden nie sichtbar, auch nicht im Portal."
      label="Notiz-Editor"
      onClose={onClose}
      primary={
        <PrimaryAction loading={pending} onClick={() => void submit()}>
          Notiz speichern
        </PrimaryAction>
      }
    >
      {banner ? <Banner tone="danger">{banner}</Banner> : null}
      <EditorField label="Text" hint="geht in die Timeline" error={errors.text}>
        <textarea
          className={styles.textarea}
          rows={4}
          aria-label="Text"
          value={text}
          onChange={(event) => setText(event.target.value)}
          autoFocus
        />
      </EditorField>
      <FollowUpField
        checked={followUp}
        onCheckedChange={setFollowUp}
        date={date}
        time={time}
        onDateChange={setDate}
        onTimeChange={setTime}
        taskTitle={taskTitle}
        error={errors.followUp}
      />
    </EditorCard>
  );
}
